import React, { useMemo, useState } from "react";
import { Box, Typography, Skeleton, Alert, InputBase } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { BORDER, BRAND, BRAND_SOFT, CHECK_STATUSES, TEXT_PRIMARY, TEXT_SECONDARY } from "./constants";

export const StatusChip = ({ status }) => {
  const config = CHECK_STATUSES[status] || CHECK_STATUSES.uploaded;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        bgcolor: config.bg,
        color: config.color,
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: config.color }} />
      {config.label}
    </Box>
  );
};

// Columns: name, date, time, created by, differences, open arrow
const GRID = { xs: "minmax(0, 1fr) auto 24px", md: "minmax(0, 1fr) 110px 70px 150px 80px 24px" };
const COLUMNS = ["שם הבדיקה", "תאריך", "שעה", "נוצר על ידי", "הבדלים", ""];

const formatDate = (value) =>
  new Date(value).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
const formatTime = (value) => new Date(value).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

// The check's own name, else the product name, else the updated file's name
export const checkTitle = (check) =>
  check.title ||
  check.product?.name ||
  (check.files?.updated?.originalName || "").replace(/\.pdf$/i, "") ||
  "בדיקה ללא שם";

const cellSx = { fontSize: 13, color: TEXT_SECONDARY, whiteSpace: "nowrap", display: { xs: "none", md: "block" } };

const RecentChecks = ({ checks, isLoading, error, onSelect }) => {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !checks) return checks || [];
    return checks.filter((check) =>
      [checkTitle(check), check.product?.name, check.product?.sku, check.createdBy?.name, check.createdBy?.email]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q)),
    );
  }, [checks, query]);

  if (error) {
    return <Alert severity="error">לא ניתן לטעון את הבדיקות הקודמות</Alert>;
  }

  if (!isLoading && !checks?.length) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: TEXT_SECONDARY }}>
        <InboxOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>עדיין לא בוצעו בדיקות</Typography>
        <Typography sx={{ fontSize: 13, mt: 0.5 }}>בדיקות שתפעילו יופיעו כאן</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          mb: 2,
          height: 44,
          maxWidth: 440,
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          transition: "border-color .15s, box-shadow .15s",
          "&:focus-within": { borderColor: BRAND, boxShadow: `0 0 0 3px ${BRAND}1f` },
        }}
      >
        <SearchRoundedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
        <InputBase
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="חיפוש לפי שם בדיקה או משתמש"
          sx={{ flex: 1, fontSize: 15 }}
          inputProps={{ "aria-label": "חיפוש בדיקות" }}
        />
      </Box>

      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
        {/* Column titles */}
        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: GRID.md,
            gap: 2,
            px: 2.5,
            py: 1.25,
            bgcolor: "#fafbfc",
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          {COLUMNS.map((title) => (
            <Typography key={title} sx={{ fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY }}>
              {title}
            </Typography>
          ))}
        </Box>

        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ px: 2.5, py: 1.75, "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` } }}>
                <Skeleton width="40%" />
              </Box>
            ))
          : visible.map((check) => {
              const inProgress = check.status !== "completed";
              const total = check.comparison?.summary?.total;
              return (
                <Box
                  key={check._id}
                  component="button"
                  type="button"
                  onClick={() => onSelect?.(check._id)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID,
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2 },
                    width: "100%",
                    px: 2.5,
                    py: 1.75,
                    border: "none",
                    borderBottom: `1px solid ${BORDER}`,
                    "&:last-of-type": { borderBottom: "none" },
                    bgcolor: "transparent",
                    font: "inherit",
                    textAlign: "start",
                    cursor: "pointer",
                    transition: "background-color .15s",
                    "&:hover": { bgcolor: BRAND_SOFT },
                    "&:hover .open-arrow": { color: BRAND, transform: "translateX(-3px)" },
                    "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      <Typography noWrap dir="auto" sx={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, textAlign: "start" }}>
                        {checkTitle(check)}
                      </Typography>
                      {/* Only checks that are still running or failed need a status */}
                      {inProgress && <StatusChip status={check.status} />}
                    </Box>
                    {/* On small screens the date and user sit under the name */}
                    <Typography noWrap sx={{ display: { xs: "block", md: "none" }, fontSize: 13, color: TEXT_SECONDARY, mt: 0.25 }}>
                      {[`${formatDate(check.createdAt)} ${formatTime(check.createdAt)}`, check.createdBy?.name].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  <Typography sx={cellSx}>{formatDate(check.createdAt)}</Typography>
                  <Typography sx={cellSx}>{formatTime(check.createdAt)}</Typography>
                  <Typography noWrap sx={cellSx}>
                    {check.createdBy?.name || check.createdBy?.email}
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: total === 0 ? "#15803d" : TEXT_PRIMARY }}>
                    {total ?? "—"}
                  </Typography>
                  <ChevronLeftRoundedIcon className="open-arrow" sx={{ color: "#cbd5e1", transition: "color .15s, transform .15s" }} />
                </Box>
              );
            })}

        {!isLoading && checks?.length > 0 && visible.length === 0 && (
          <Typography sx={{ py: 4, textAlign: "center", fontSize: 15, color: TEXT_SECONDARY }}>
            לא נמצאו בדיקות התואמות לחיפוש
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RecentChecks;
